<template>
  <div class="md-layout product-form-page">
    <div class="md-layout-item">
      <md-card class="form-card" v-if="product._id">
        <md-card-header class="form-card-header" :class="headerClass">
          <div class="header-icon-wrap"><md-icon>edit</md-icon></div>
          <div class="header-text">
            <h4 class="title">Edit Product</h4>
            <p class="subtitle">{{ product.displayName || product.name }}</p>
          </div>
          <span class="type-pill">{{ product.productType }}</span>
        </md-card-header>

        <md-card-content class="form-body">
          <!-- Basic Info -->
          <div class="form-section">
            <h5 class="section-title"><md-icon>info_outline</md-icon> Basic Information</h5>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Product Name <span class="req">*</span></label>
                <input v-model="product.name" class="field-input" />
              </div>
              <div class="form-group">
                <label class="field-label">Display Name <span class="req">*</span></label>
                <input v-model="product.displayName" class="field-input" />
              </div>
            </div>
            <div class="form-group">
              <label class="field-label">Short Description</label>
              <textarea v-model="product.shortDescription" class="field-input field-textarea" rows="3" />
            </div>
            <div class="form-group">
              <label class="field-label">Long Description</label>
              <textarea v-model="product.longDescription" class="field-input field-textarea" rows="4" />
            </div>
            <div class="form-row two-col">
              <div class="form-group" v-if="product.productType !== 'Accessories'">
                <label class="field-label">Product Category</label>
                <select v-model="product.productCategory" class="field-input field-select">
                  <option value="">- select -</option>
                  <option value="Phones">Phones</option>
                  <option value="Tablets">Tablets</option>
                </select>
              </div>
              <div class="form-group" v-if="product.productType !== 'Accessories' && product.productType !== 'Subscription'">
                <label class="field-label">GTIN</label>
                <input v-model="product.gtin" class="field-input" placeholder="13-digit barcode" />
              </div>
            </div>
          </div>

          <!-- Device Specs -->
          <div class="form-section" v-if="product.productType !== 'Subscription'">
            <h5 class="section-title"><md-icon>smartphone</md-icon> Device Specifications</h5>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Camera</label>
                <input v-model="product.camera" class="field-input" placeholder="e.g. 50MP + 5MP" />
              </div>
              <div class="form-group">
                <label class="field-label">Screen</label>
                <input v-model="product.screen" class="field-input" placeholder="e.g. 6.5 FHD+ AMOLED" />
              </div>
            </div>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Memory / Storage</label>
                <input v-model="product.memory" class="field-input" placeholder="e.g. 4GB RAM / 128GB" />
              </div>
              <div class="form-group">
                <label class="field-label">Battery</label>
                <input v-model="product.battery" class="field-input" placeholder="e.g. 5000 mAh" />
              </div>
            </div>
          </div>

          <!-- Compatible Products (Accessories) -->
          <div class="form-section" v-if="product.productType === 'Accessories'">
            <h5 class="section-title"><md-icon>link</md-icon> Compatible Products</h5>
            <p class="section-desc">Select which one-time products this accessory belongs to.</p>
            <div v-if="onetimeProducts.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon> No one-time products found.
            </div>
            <div class="parent-products-grid">
              <label
                v-for="p in onetimeProducts"
                :key="p._id"
                class="parent-product-card"
                :class="{ selected: (product.parentProductIds || []).map(String).includes(String(p._id)) }"
              >
                <input type="checkbox" :value="p._id" v-model="product.parentProductIds" style="display: none" />
                <div class="pp-avatar">{{ (p.name || "?")[0].toUpperCase() }}</div>
                <div class="pp-info">
                  <strong>{{ p.displayName || p.name }}</strong>
                  <small>{{ p.name }}</small>
                </div>
                <md-icon class="pp-check">
                  {{ (product.parentProductIds || []).map(String).includes(String(p._id)) ? "check_circle" : "radio_button_unchecked" }}
                </md-icon>
              </label>
            </div>
          </div>

          <!-- Custom Specifications -->
          <div class="form-section">
            <h5 class="section-title"><md-icon>list_alt</md-icon> Custom Specifications</h5>
            <div v-if="(product.productSpecifications || []).length === 0" class="empty-specs">
              <md-icon>list_alt</md-icon>
              <p>No custom specifications yet.</p>
            </div>
            <div v-for="(spec, i) in product.productSpecifications" :key="i" class="spec-row">
              <input v-model="spec.key" class="field-input spec-input" placeholder="Key (e.g. OS)" />
              <input v-model="spec.value" class="field-input spec-input" placeholder="Value (e.g. Android 14)" />
              <button class="remove-spec-btn" @click="removeSpec(i)">
                <md-icon>delete_outline</md-icon>
              </button>
            </div>
            <button class="add-spec-btn" @click="addSpec">
              <md-icon>add</md-icon> Add Specification
            </button>
          </div>

          <!-- Shipping Methods -->
          <div class="form-section" v-if="product.productType !== 'Subscription'">
            <h5 class="section-title"><md-icon>local_shipping</md-icon> Shipping Methods</h5>
            <p class="section-desc">Select allowed shipping methods. Leave all unchecked to allow all active methods.</p>
            <div v-if="loadingShipping" class="shipping-loading">
              <md-progress-spinner md-mode="indeterminate" :md-diameter="24" :md-stroke="2" />
              <span>Loading...</span>
            </div>
            <div v-else-if="shippingMethods.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon> No shipping methods configured yet.
            </div>
            <div v-else>
              <div class="shipping-all-banner" v-if="!product.allowedShippingMethods || product.allowedShippingMethods.length === 0">
                <md-icon>check_circle</md-icon>
                All active shipping methods are allowed for this product.
              </div>
              <div
                v-for="method in shippingMethods"
                :key="method._id"
                class="shipping-method-row"
                :class="{ selected: isShippingSelected(method._id) }"
                @click="toggleShipping(method._id)"
              >
                <div class="shipping-check">
                  <md-icon>{{ isShippingSelected(method._id) ? "check_box" : "check_box_outline_blank" }}</md-icon>
                </div>
                <div class="shipping-icon-wrap">
                  <md-icon>{{ method.icon || "local_shipping" }}</md-icon>
                </div>
                <div class="shipping-info">
                  <strong>{{ method.name }}</strong>
                  <small>{{ method.description || method.type }}</small>
                </div>
                <span class="type-pill" :class="'type-' + method.type">{{ method.type }}</span>
              </div>
              <div class="shipping-hint">
                <md-icon>info_outline</md-icon>
                <span>Uncheck all to allow every method. Check specific ones to restrict this product.</span>
              </div>
            </div>
          </div>

          <!-- Google Merchant Sync (Onetime products only) -->
          <div class="form-section" v-if="product.productType === 'Onetime'">
            <h5 class="section-title"><md-icon>shopping_bag</md-icon> Google Merchant Center</h5>
            <p class="section-desc">
              Sync this product to Google Merchant Center for all configured markets.
              Creates new listings where missing, updates existing ones with latest price and details.
            </p>

            <!-- Sync results -->
            <div v-if="syncResults.length > 0" class="sync-results">
              <div
                v-for="market in syncResults"
                :key="market.marketplaceCode"
                class="sync-result-row"
                :class="'sync-' + market.action"
              >
                <md-icon class="sync-result-icon">
                  {{ market.action === 'created' ? 'add_circle' : market.action === 'updated' ? 'check_circle' : market.action === 'skipped' ? 'skip_next' : 'error_outline' }}
                </md-icon>
                <div class="sync-result-info">
                  <strong>{{ market.marketplaceCode }}</strong>
                  <small v-if="market.action === 'created'">Listed on Google Merchant</small>
                  <small v-else-if="market.action === 'updated'">Updated on Google Merchant</small>
                  <small v-else-if="market.action === 'skipped'">{{ market.error || 'No price configured for this market' }}</small>
                  <small v-else class="error-text">{{ market.error }}</small>
                </div>
                <span class="sync-badge" :class="'sync-badge-' + market.action">{{ market.action }}</span>
              </div>
            </div>

            <button class="btn-sync" :disabled="syncing" @click="syncGoogleMerchant">
              <i v-if="syncing" class="fa fa-spinner fa-spin"></i>
              <md-icon v-else>sync</md-icon>
              {{ syncing ? "Syncing..." : "Sync to Google Merchant" }}
            </button>
          </div>

          <!-- Main Image -->
          <div class="form-section">
            <h5 class="section-title"><md-icon>photo_library</md-icon> Main Image</h5>
            <div class="image-upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="onDrop">
              <div v-if="imagePreview || product.mainImage" class="image-upload-preview">
                <img :src="imagePreview || fullImageUrl(product.mainImage)" class="preview-img" />
                <button class="remove-preview" @click.stop="removeImage">
                  <md-icon>close</md-icon>
                </button>
              </div>
              <div v-else class="upload-placeholder">
                <md-icon class="upload-cloud-icon">cloud_upload</md-icon>
                <p>Click or drag &amp; drop to replace image</p>
                <small>PNG, JPG, WEBP up to 5MB</small>
              </div>
              <input ref="fileInput" type="file" accept="image/*" @change="handleImageUpload" style="display: none" />
            </div>
          </div>

          <!-- Actions -->
          <div class="form-actions">
            <button class="btn-cancel" @click="$router.push('/kid/wingio-products')">Cancel</button>
            <button class="btn-save" :disabled="saving" @click="saveProduct">
              <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
              <span v-else><md-icon>save</md-icon> Save Changes</span>
            </button>
          </div>
        </md-card-content>
      </md-card>

      <div v-else class="loading-wrap">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="40" :md-stroke="3" />
      </div>
    </div>
  </div>
</template>

<script>
import ProductService from "../../services/SuperAdmin/Kids/ProductService";
import ShippingService from "../../services/SuperAdmin/Kids/ShippingService";

export default {
  data() {
    return {
      saving: false,
      syncing: false,
      syncResults: [],
      loadingShipping: false,
      productId: this.$route.query.id,
      imageFile: null,
      imagePreview: null,
      onetimeProducts: [],
      shippingMethods: [],
      product: { _id: "" },
    };
  },
  computed: {
    headerClass() {
      var map = {
        Onetime: "header-onetime",
        Subscription: "header-subscription",
        "Lifetime Subscription": "header-lifetime",
        Accessories: "header-accessories",
      };
      return map[this.product.productType] || "header-onetime";
    },
  },
  methods: {
    fullImageUrl(filename) {
      if (!filename) return "";
      if (filename.startsWith("http")) return filename;
      return (process.env.VUE_APP_API_URL || "") + "images/" + filename;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleImageUpload(e) {
      var file = e.target.files[0];
      if (!file) return;
      this.imageFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },
    onDrop(e) {
      var file = e.dataTransfer.files[0];
      if (!file || !file.type.startsWith("image/")) return;
      this.imageFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },
    removeImage() {
      this.imageFile = null;
      this.imagePreview = null;
      this.product.mainImage = "";
    },
    addSpec() {
      if (!this.product.productSpecifications) this.product.productSpecifications = [];
      this.product.productSpecifications.push({ key: "", value: "" });
    },
    removeSpec(i) {
      this.product.productSpecifications.splice(i, 1);
    },
    isShippingSelected(id) {
      if (!this.product.allowedShippingMethods || this.product.allowedShippingMethods.length === 0) return false;
      var idStr = String(id);
      return this.product.allowedShippingMethods.some(function (existing) {
        return String(existing) === idStr;
      });
    },
    toggleShipping(id) {
      if (!this.product.allowedShippingMethods) this.product.allowedShippingMethods = [];
      var idStr = String(id);
      var idx = this.product.allowedShippingMethods.findIndex(function (existing) {
        return String(existing) === idStr;
      });
      if (idx > -1) {
        this.product.allowedShippingMethods.splice(idx, 1);
      } else {
        this.product.allowedShippingMethods.push(idStr);
      }
    },
    loadShippingMethods() {
      this.loadingShipping = true;
      ShippingService.listMethods()
        .then(function (r) {
          this.shippingMethods = (r.data.data || []).filter(function (m) { return m.isActive; });
        }.bind(this))
        .catch(function () {})
        .finally(function () { this.loadingShipping = false; }.bind(this));
    },
    loadProduct() {
      ProductService.getProductImages({ id: this.productId })
        .then((r) => {
          this.product = {
            ...r.data.produtDetail,
            productSpecifications: r.data.produtDetail.productSpecifications || [],
            parentProductIds: (r.data.produtDetail.parentProductIds || []).map(String),
            allowedShippingMethods: (r.data.produtDetail.allowedShippingMethods || []).map(function (id) {
              return String(id && id._id ? id._id : id);
            }),
          };
          if (this.product.productType === "Accessories") this.loadOnetimeProducts();
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) || "Failed to load product"
          );
        });
    },
    loadOnetimeProducts() {
      ProductService.getOnetimeProducts()
        .then((r) => { this.onetimeProducts = r.data.data; })
        .catch(() => {});
    },
    syncGoogleMerchant() {
      this.syncing = true;
      this.syncResults = [];
      ProductService.syncGoogleMerchant({ productId: this.product._id })
        .then((r) => {
          var productResult = r.data.data && r.data.data[0];
          this.syncResults = (productResult && productResult.markets) || [];
          var summary = r.data.summary || {};
          this.$toast.success(
            "Sync done — Created: " + (summary.created || 0) +
            ", Updated: " + (summary.updated || 0) +
            ", Skipped: " + (summary.skipped || 0) +
            ", Failed: " + (summary.failed || 0)
          );
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) || "Sync failed."
          );
        })
        .finally(() => { this.syncing = false; });
    },
    async saveProduct() {
      if (!this.product.name || !this.product.name.trim() ||
          !this.product.displayName || !this.product.displayName.trim()) {
        this.$toast.error("Name and Display Name are required.");
        return;
      }
      this.saving = true;
      if (this.imageFile) {
        try {
          var fd = new FormData();
          fd.append("images[]", this.imageFile);
          fd.append("productId", this.product._id);
          var uploadRes = await ProductService.uploadProductImages(fd);
          var uploaded = uploadRes.data && uploadRes.data.data && uploadRes.data.data[0];
          if (uploaded && uploaded.productImage) this.product.mainImage = uploaded.productImage;
        } catch (e) {
          this.$toast.error("Image upload failed.");
          this.saving = false;
          return;
        }
      }
      ProductService.updateProduct(this.product)
        .then(() => {
          this.$toast.success("Product updated successfully!");
          this.saving = false;
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) || "Update failed."
          );
          this.saving = false;
        });
    },
  },
  beforeMount() {
    this.loadProduct();
    this.loadShippingMethods();
  },
};
</script>

<style scoped>
.product-form-page {
  font-family: "Segoe UI", sans-serif;
}

.form-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 28px;
  color: white;
}

.header-onetime {
  background: linear-gradient(135deg, #43a047, #1b5e20);
}

.header-subscription {
  background: linear-gradient(135deg, #1976d2, #0d47a1);
}

.header-lifetime {
  background: linear-gradient(135deg, #f57c00, #bf360c);
}

.header-accessories {
  background: linear-gradient(135deg, #8e24aa, #4a148c);
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon-wrap .md-icon {
  color: white !important;
  font-size: 24px !important;
}

.header-text {
  flex: 1;
}

.form-card-header .title {
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.form-card-header .subtitle {
  color: rgba(255, 255, 255, 0.75);
  margin: 2px 0 0;
  font-size: 13px;
}

.type-pill {
  padding: 4px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.form-body {
  padding: 0;
}

.form-section {
  padding: 24px 28px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #444;
  margin: 0 0 16px;
}

.section-title .md-icon {
  font-size: 18px !important;
  color: #888 !important;
}

.section-desc {
  font-size: 13px;
  color: #888;
  margin: -8px 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row.two-col > * {
  flex: 1;
}

.form-group {
  margin-bottom: 16px;
  flex: 1;
}

.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #666;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.req {
  color: #e53935;
}

.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}

.field-input:focus {
  border-color: #43a047;
}

.field-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.field-select {
  -webkit-appearance: none;
  cursor: pointer;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #e3f2fd;
  border-radius: 8px;
  font-size: 14px;
  color: #1565c0;
  margin-bottom: 12px;
}

.parent-products-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.parent-product-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 15px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  background: white;
}

.parent-product-card:hover {
  border-color: #9c27b0;
  background: #fdf3ff;
}

.parent-product-card.selected {
  border-color: #9c27b0;
  background: #f3e5f5;
}

.pp-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
}

.pp-info {
  flex: 1;
}

.pp-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}

.pp-info small {
  color: #888;
  font-size: 12px;
}

.pp-check {
  color: #9c27b0 !important;
  font-size: 20px !important;
}

.empty-specs {
  text-align: center;
  padding: 20px;
  color: #ccc;
}

.empty-specs .md-icon {
  font-size: 30px !important;
  color: #ddd !important;
  display: block;
  margin-bottom: 4px;
}

.empty-specs p {
  margin: 0;
  font-size: 13px;
}

.spec-row {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.spec-input {
  flex: 1;
}

.remove-spec-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: #e53935;
  border-radius: 6px;
}

.remove-spec-btn:hover {
  background: #ffebee;
}

.remove-spec-btn .md-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}

.add-spec-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1.5px dashed #43a047;
  background: transparent;
  border-radius: 8px;
  color: #43a047;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.add-spec-btn:hover {
  background: #e8f5e9;
}

.add-spec-btn .md-icon {
  color: #43a047 !important;
  font-size: 17px !important;
}

.shipping-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  color: #888;
  font-size: 14px;
}

.shipping-method-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.shipping-method-row:hover {
  border-color: #43a047;
  background: #f9fff9;
}

.shipping-method-row.selected {
  border-color: #43a047;
  background: #e8f5e9;
}

.shipping-check .md-icon {
  color: #43a047 !important;
  font-size: 22px !important;
}

.shipping-icon-wrap {
  width: 36px;
  height: 36px;
  background: #e3f2fd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.shipping-icon-wrap .md-icon {
  color: #0277bd !important;
  font-size: 18px !important;
}

.shipping-info {
  flex: 1;
}

.shipping-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}

.shipping-info small {
  color: #888;
  font-size: 12px;
}

.shipping-all-banner {
  gap: 8px;
  padding: 10px 14px;
  background: #e8f5e9;
  border-radius: 8px;
  font-size: 13px;
  color: #2e7d32;
  margin-bottom: 12px;
}

.shipping-all-banner .md-icon {
  color: #2e7d32 !important;
  font-size: 18px !important;
}

.shipping-hint {
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  color: #aaa;
}

.shipping-hint .md-icon {
  color: #ccc !important;
  font-size: 16px !important;
}

.sync-results {
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.sync-result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
}

.sync-result-row:last-child {
  border-bottom: none;
}

.sync-created {
  background: #f1f8e9;
}

.sync-updated {
  background: #e3f2fd;
}

.sync-skipped {
  background: #fafafa;
}

.sync-failed {
  background: #ffebee;
}

.sync-result-icon {
  font-size: 20px !important;
  flex-shrink: 0;
}

.sync-created .sync-result-icon {
  color: #43a047 !important;
}

.sync-updated .sync-result-icon {
  color: #1976d2 !important;
}

.sync-skipped .sync-result-icon {
  color: #aaa !important;
}

.sync-failed .sync-result-icon {
  color: #e53935 !important;
}

.sync-result-info {
  flex: 1;
}

.sync-result-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}

.sync-result-info small {
  font-size: 12px;
  color: #888;
}

.sync-result-info .error-text {
  color: #e53935;
}

.sync-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.sync-badge-created {
  background: #e8f5e9;
  color: #2e7d32;
}

.sync-badge-updated {
  background: #e3f2fd;
  color: #0277bd;
}

.sync-badge-skipped {
  background: #f5f5f5;
  color: #888;
}

.sync-badge-failed {
  background: #ffebee;
  color: #c62828;
}

.btn-sync {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 9px;
  border: 1.5px solid #1a73e8;
  background: white;
  color: #1a73e8;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-sync:hover:not(:disabled) {
  background: #e8f0fe;
}

.btn-sync:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-sync .md-icon {
  color: #1a73e8 !important;
  font-size: 18px !important;
}

.image-upload-area {
  border: 2px dashed #ccc;
  border-radius: 12px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
  position: relative;
}

.image-upload-area:hover {
  border-color: #43a047;
  background: #f9fff9;
}

.upload-placeholder {
  text-align: center;
  padding: 40px;
}

.upload-cloud-icon {
  font-size: 40px !important;
  color: #ccc !important;
  margin-bottom: 8px;
  display: block;
}

.upload-placeholder p {
  color: #888;
  margin: 8px 0 4px;
}

.upload-placeholder small {
  color: #bbb;
}

.image-upload-preview {
  position: relative;
}

.preview-img {
  max-height: 200px;
  max-width: 100%;
  border-radius: 8px;
  display: block;
}

.remove-preview {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-preview .md-icon {
  color: white !important;
  font-size: 16px !important;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px 28px;
}

.btn-cancel {
  padding: 10px 22px;
  border-radius: 9px;
  border: 1.5px solid #e0e0e0;
  background: white;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f5f5f5;
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  border-radius: 9px;
  border: none;
  background: #43a047;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save:hover:not(:disabled) {
  background: #388e3c;
}

.btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-save .md-icon {
  color: white !important;
  font-size: 18px !important;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 80px;
}
</style>